/**
 * <yo-marquee> — a reusable infinite scroller.
 *
 * All motion is in marquee.css. This element does three things: wrap its
 * children in a repeatable group, clone that group until the row overflows,
 * and translate a px/second speed into an animation duration.
 *
 *   <yo-marquee speed="60" direction="right" gap="3rem" fade pause-on-hover>
 *     <span>Item</span><span>Item</span>
 *   </yo-marquee>
 *
 * Attributes:
 *   speed (px/sec)
 *   direction (left|right|up|down)
 *   gap
 *   height
 *   fade
 *   pause-on-hover
 *
 * Call .reset() to remeasure and restart by hand.
 */

const DEFAULT_SPEED = 60; // px per second
const RESIZE_DEBOUNCE = 150; // ms to let a resize gesture settle

const VERTICAL = new Set(['up', 'down']);

export class YO_Marquee extends HTMLElement {
  static observedAttributes = ['speed', 'direction', 'gap', 'height'];

  #group = null;
  #resizeObserver = null;
  #mutationObserver = null;
  #resizeTimer = 0;
  /** Nesting depth of #mutate, so re-entrant writes re-observe only once. */
  #mutationDepth = 0;
  /** Last measured box, so a no-op resize doesn't restart the animation. */
  #measured = { viewport: 0, group: 0 };

  connectedCallback() {
    // Re-parenting fires disconnect then connect. Adopting again would wrap
    // the existing group in a second one, so only build if we have no group.
    if (!this.#group?.isConnected) this.#adopt();
    this.reset();

    // Container-driven size changes (sidebar opens, grid reflows, ...).
    this.#resizeObserver = new ResizeObserver(() => this.#scheduleReset());
    this.#resizeObserver.observe(this);

    // Content changes from a framework re-render. Our own writes go through
    // #mutate, which detaches this observer, so anything reaching the callback
    // came from outside.
    this.#mutationObserver = new MutationObserver(() => this.#rebuild());
    this.#mutationObserver.observe(this, { childList: true });

    // Window-level changes: zoom, orientation, and viewport-relative units
    // that a ResizeObserver on this element may not see.
    window.addEventListener('resize', this.#onWindowResize);
    window.addEventListener('orientationchange', this.#onWindowResize);

    // Late-loading fonts change content width after first measurement.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.reset();
    });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    window.removeEventListener('resize', this.#onWindowResize);
    window.removeEventListener('orientationchange', this.#onWindowResize);
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = 0;
  }

  attributeChangedCallback(name, previous, value) {
    if (previous === value) return;

    // gap and height are pure CSS knobs; forward them to the custom property.
    if (name === 'gap') this.style.setProperty('--marquee-gap', value ?? '');
    if (name === 'height') this.style.setProperty('--marquee-height', value ?? '');

    if (this.isConnected) this.reset();
  }

  get speed() {
    return Number(this.getAttribute('speed')) || DEFAULT_SPEED;
  }

  set speed(value) {
    this.setAttribute('speed', value);
  }

  get direction() {
    return this.getAttribute('direction') || 'left';
  }

  set direction(value) {
    this.setAttribute('direction', value);
  }

  /**
   * Remeasure, re-clone, and restart every group in phase.
   * Skips the work when nothing that affects layout has changed, unless
   * `force` is set — attribute and manual resets always go through.
   */
  reset({ force = true } = {}) {
    const group = this.#group;
    if (!group || !this.isConnected) return;

    // Measured before touching the clones: the group sizes itself to its own
    // content (min-width: max-content), so the clones don't skew it. That
    // means an unchanged box can bail out having disturbed nothing.
    const vertical = VERTICAL.has(this.direction);
    const style = getComputedStyle(this);
    // Custom properties resolve to their raw token ("2rem"), but the flex gap
    // they feed is computed to px for us.
    const gap = parseFloat(vertical ? style.rowGap : style.columnGap) || 0;
    const viewport = vertical ? this.clientHeight : this.clientWidth;
    const box = group.getBoundingClientRect();
    const groupSize = vertical ? box.height : box.width;
    if (!groupSize) return;

    if (!force && viewport === this.#measured.viewport && groupSize === this.#measured.group) {
      return;
    }
    this.#measured = { viewport, group: groupSize };

    this.#mutate(() => {
      for (const clone of this.querySelectorAll(':scope > [aria-hidden="true"]')) {
        clone.remove();
      }

      // Each cycle shifts everything by one group, so we need enough copies to
      // still cover the viewport after that shift: ceil(viewport / group) + 1.
      const copies = Math.ceil(viewport / (groupSize + gap)) + 1;
      const batch = document.createDocumentFragment();
      for (let i = 1; i < copies; i++) {
        batch.appendChild(this.#clone(group));
      }
      this.appendChild(batch);

      this.style.setProperty('--marquee-duration', `${(groupSize + gap) / this.speed}s`);
      this.#restart();
    });
  }

  /**
   * Run a DOM write without the MutationObserver seeing it. A boolean flag
   * cannot do this: observer callbacks arrive as microtasks, long after a
   * synchronous finally would have cleared it. Detaching drops the queued
   * records outright.
   */
  #mutate(write) {
    if (this.#mutationDepth++ === 0) this.#mutationObserver?.disconnect();
    try {
      write();
    } finally {
      if (--this.#mutationDepth === 0) {
        this.#mutationObserver?.observe(this, { childList: true });
      }
    }
  }

  /** Wrap the element's own children in the group the CSS animates. */
  #adopt() {
    this.#mutate(() => {
      const group = document.createElement('div');
      group.className = 'marquee__group';
      group.append(...this.childNodes);
      this.appendChild(group);
      this.#group = group;
    });
    this.#awaitMedia();
  }

  /**
   * An image that lands after the first measurement changes the group width
   * and invalidates the clone count. Authors should size their images in CSS
   * so this never fires, but unsized images must not be left broken.
   */
  #awaitMedia() {
    for (const img of this.#group?.querySelectorAll('img') ?? []) {
      if (img.complete) continue;
      img.addEventListener('load', this.#onMediaLoad, { once: true });
      img.addEventListener('error', this.#onMediaLoad, { once: true });
    }
  }

  /** Debounced, so a row of images settles into one reset rather than N. */
  #onMediaLoad = () => this.#scheduleReset(true);

  /** Unwrap back to the author's content, then build again from scratch. */
  #rebuild() {
    this.#mutate(() => {
      for (const group of this.querySelectorAll(':scope > .marquee__group')) {
        if (group.getAttribute('aria-hidden') === 'true') group.remove();
        else group.replaceWith(...group.childNodes);
      }
      this.#group = null;
      this.#adopt();
      this.reset();
    });
  }

  #clone(group) {
    const clone = group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    // Clones are decorative; keep them out of the tab order.
    for (const node of clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
      node.setAttribute('tabindex', '-1');
    }
    return clone;
  }

  /**
   * Restart all groups together. Without this, clones added during a reset
   * begin at t=0 while the original group is mid-cycle, and the two drift
   * out of phase — the seam becomes visible.
   */
  #restart() {
    const groups = this.querySelectorAll(':scope > .marquee__group');
    for (const group of groups) group.style.animation = 'none';
    void this.offsetWidth; // force reflow so the removal takes effect
    for (const group of groups) group.style.animation = '';
  }

  #scheduleReset = (force = false) => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.reset({ force }), RESIZE_DEBOUNCE);
  };

  #onWindowResize = () => this.#scheduleReset(true);
}

if (!customElements.get('yo-marquee')) {
  customElements.define('yo-marquee', YO_Marquee);
}
